const axios = require('axios');

const RECIPE_IMAGE_PLACEHOLDER = '/img/recipe-placeholder.jpg';

const REFS = {
    User: '/users',
    InitialRecommendations: '/initial-recommendations',
    Product: '/products',
    Recipe: '/recipes',
    Programm: '/programms',
};

class ApiClient {
    constructor(basePath, staticDownloadPath, paymentIntegration) {
        this.basePath = basePath;
        this.refs = REFS;
        this.paymentIntegration = paymentIntegration;
        this.requestClient = axios.create({
            baseURL: this.basePath
        });
        this.staticBucketDownloadPath = staticDownloadPath;
    }

    createImageHrefs(images) {
        return Array.isArray(images) ? images.map(image => `${this.staticBucketDownloadPath}/${image}`) : [];
    }

    async request({
        path,
        method = 'get',
        body,
        query,
        token
    } = {}) {
        const headers = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await this.requestClient({
            url: path,
            method,
            params: query,
            data: body,
            headers
        });
        return response.data;
    }

    async login(params) {
        const body = await this.request({
            path: '/login',
            method: 'post',
            body: params,
        });
        return body.result;
    }

    async restore(params) {
        const body = await this.request({
            path: '/reset_password',
            method: 'post',
            body: params,
        });
        return body.result;
    }

    async changePasswordWithCode(code, params) {
        const body = await this.request({
            path: `/change_password/${code}`,
            method: 'put',
            body: params,
        });
        return body.result;
    }

    async verifyToken(token) {
        try {
            const body = await this.request({
                path: `/verify`,
                method: 'get',
                token,
            })
            return body.result;
        } catch (error) {
            console.error('Token verification failed', error);
            return;
        }
    }

    async getSelf(token) {
        const body = await this.request({
            path: '/users/~/',
            method: 'get',
            token: token,
        });
        if (body.result && body.result.name) {
            body.result.firstLetter = body.result.name[0].toUpperCase();
        }
        // Кабинет эксперта открывается тому, у кого есть роль и код:
        // по коду к эксперту привязываются клиенты, без кода привязывать
        // некого. Ту же проверку делает bod-users, здесь — чтобы не
        // показывать пункт меню, которым нельзя воспользоваться.
        //
        // Роли `expert` и `trainer` равны: они разошлись исторически,
        // и кабинет открывают обе (решение владельца 12.10). У кого
        // роли нет, тот своих приглашённых не видит, даже если они есть.
        if (body.result) {
            const roles = body.result.roles || [];
            body.result.isExpert = !!body.result.code
                && (roles.includes('expert')
                    || roles.includes('trainer')
                    || roles.includes('admin'));
        }
        return body.result;
    }

    // Клиенты эксперта для таблицы «Мои клиенты».
    async getExpertClients(token) {
        const body = await this.request({
            path: '/users/clients/',
            method: 'get',
            token: token,
        });
        return body.result || [];
    }

    // Партнёры эксперта: кого он привёл в сеть. Отдаётся меньше
    // полей, чем по своим клиентам, — только имя, партнёр и оплата.
    async getNetworkClients(token) {
        const body = await this.request({
            path: '/users/network-clients/',
            method: 'get',
            token: token,
        });
        return body.result || [];
    }



    // Кто стоит за кодом эксперта — для страницы `/partner/<код>`:
    // человек должен видеть, к кому идёт, до заполнения анкеты.
    // Ручка открытая, поэтому сервер отдаёт только имя и тип.
    async getExpertByCode(code) {
        const body = await this.request({
            path: `/experts/${encodeURIComponent(code)}/`,
            method: 'get',
        });
        return body.result;
    }

    async getUserParams(type, age, token) {
        const body = await this.request({
            path: `/user-params/type/${type}/age/${age}`,
            method: 'get',
            token: token,
        });
        return body.result;
    }

    async getProgrammsByIds(ids, token) {
        const body = await this.request({
            path: `/programms/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [body.result];
    }
    
    async updateProgramm(programmId, params, token) {
        const body = await this.request({
            path: `/programms/${programmId}`,
            method: 'patch',
            body: params,
            token: token,
        });
        return body.result;
    }
    
    async adjustProgramm(programmId, token) {
        const body = await this.request({
            path: '/programm-tasks/user/me/adjust/',
            method: 'post',
            body: {
                programmId
            },
            token: token,
        });
        return body.result;
    }

    async getTariffsByIds(ids, token) {
        const body = await this.request({
            path: `/tariffs/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [body.result];
    }

    async confirmEmail(code) {
        const body = await this.request({
            path: `/users/confirm-email/${code}`,
            method: 'put'
        });
        return Array.isArray(body.result) ? body.result : [body.result];
    }

    async getPayments(userId, token) {
        const body = await this.request({
            path: `/users/${userId}/payments`,
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [body.result];
    }

    async getKitchenware(token) {
        const body = await this.request({
            path: '/kitchenware',
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [body.result];
    }

    // Справочники целей и вариантов питания. Нужны и анкете в личном
    // кабинете, и форме регистрации — а она анонимна, поэтому токен
    // необязателен.
    async getGoals(token) {
        const body = await this.request({
            path: '/goals',
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [];
    }

    async getActivityQuestions(token) {
        const body = await this.request({
            path: '/activity-questions',
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [];
    }

    async getDietTypes(token) {
        const body = await this.request({
            path: '/diet-types',
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [];
    }

    async getExercisesByIds(ids, token) {
        const body = await this.request({
            path: `/exercises/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return (Array.isArray(body.result) ? body.result : [body.result]).map(exercise => {
            exercise.imageHrefs = this.createImageHrefs(exercise.images);
            return exercise;
        });
    }

    async getTrainingsByIds(ids, token) {
        const body = await this.request({
            path: `/trainings/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return Array.isArray(body.result) ? body.result : [body.result];
    }

    async getPracticesByIds(ids, token) {
        const body = await this.request({
            path: `/practices/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return (Array.isArray(body.result) ? body.result : [body.result]).map(practice => {
            practice.imageHrefs = this.createImageHrefs(practice.images);
            return practice;
        });
    }

    async getRecipesByIds(ids, token) {
        const body = await this.request({
            path: `/recipes/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return (Array.isArray(body.result) ? body.result : [body.result]).map(recipe => {
            recipe.imageHrefs = this.createImageHrefs(recipe.images);
            // Рецепт без фото — заглушка вместо пустой серой карточки.
            // Нужна, пока у новых рецептов не загружены фото.
            if (!recipe.imageHrefs.length) recipe.imageHrefs = [RECIPE_IMAGE_PLACEHOLDER];
            return recipe;
        });
    }

    async getProductsByIds(ids, token) {
        const body = await this.request({
            path: `/products/${ids.join(',')}`,
            method: 'get',
            token: token,
        });
        return (Array.isArray(body.result) ? body.result : [body.result]).map(product => {
            product.imageHrefs = this.createImageHrefs(product.images);
            return product;
        });
    }

    async getProducts(token, sortByName = false) {
        const body = await this.request({
            path: `/products`,
            method: 'get',
            query: sortByName ? { sort: 'title' } : {},
            token: token,
        });
        return (Array.isArray(body.result) ? body.result : [body.result]).map(product => {
            product.imageHrefs = this.createImageHrefs(product.images);
            return product;
        });
    }

    async createUser(object, token) {
        const body = await this.request({
            path: REFS.User,
            method: 'post',
            body: object,
            token,
        });
        return body.result;
    }

    async updateUser(object, token) {
        const body = await this.request({
            path: `${REFS.User}/~`,
            method: 'patch',
            body: object,
            token,
        });
        return body.result;
    }

    // Карточка клиента в кабинете эксперта: читаем и правим чужого
    // пользователя. Право на это проверяет bod-users — клиент должен
    // быть привязан к этому эксперту.
    async getUserById(userId, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}`,
            method: 'get',
            token,
        });
        return body.result;
    }

    async updateUserById(userId, object, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}`,
            method: 'patch',
            body: object,
            token,
        });
        return body.result;
    }

    // Раскладка вариантов дня плана по дням недели — окно «Настроить
    // неделю». Отправляем все варианты, включая те, что остались
    // без дней: иначе они пропадут из профиля и человек не сможет
    // вернуть их обратно (bod-knowledge decisions/week-day-variants.md).
    async setUserProgramms(userId, programms, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}/programms`,
            method: 'put',
            body: { programms },
            token,
        });
        return body.result;
    }

    // Отметки в списке покупок. Ключ — вариант дня плана плюс продукт:
    // один продукт попадает в несколько вариантов, а закупают
    // их отдельно (bod-knowledge decisions/shopping-list-structure.md).
    async setShoppingMarks(userId, marks, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}/shopping-marks`,
            method: 'put',
            body: { marks },
            token,
        });
        return body.result;
    }

    // Разделы рекомендаций для текущего человека. Цель и пол сервер
    // берёт из его же карточки — набор разделов не выбирается запросом.
    async getMyRecommendationSections(token) {
        const body = await this.request({
            path: `${REFS.InitialRecommendations}/my/`,
            method: 'get',
            token,
        });
        return (body.result || {}).sections || [];
    }

    // Отметка прочитанных разделов. Коды только добавляются: раздел,
    // который человек открыл, остаётся прочитанным.
    async addRecommendationsRead(userId, codes, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}/recommendations-read`,
            method: 'put',
            body: { codes },
            token,
        });
        return body.result;
    }

    // Состояние программы клиента для виджета «Обновить программу».
    async getProgrammsStatus(userId, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}/programms-status/`,
            method: 'get',
            token,
        });
        return body.result;
    }

    async rebuildProgramms(userId, token) {
        const body = await this.request({
            path: `${REFS.User}/${userId}/rebuild-programms/`,
            method: 'post',
            token,
        });
        return body.result;
    }

    // Ограничения необязательные, но клиенту нужны все: замена должна
    // подходить тому же приёму пищи, варианту питания и не содержать
    // исключённых продуктов.
    async getSimilarRecipes(recipeId, deviation = 0.1, token, limits = {}) {
        const query = { deviation };
        if (limits.mealTypes) query.mealTypes = limits.mealTypes;
        if (limits.dietTypeCode) query.dietTypeCode = limits.dietTypeCode;
        if (limits.excludeProductIds && limits.excludeProductIds.length) {
            query.excludeProductIds = limits.excludeProductIds.join(',');
        }
        const body = await this.request({
            path: `${REFS.Recipe}/${recipeId}/similar`,
            method: 'get',
            query,
            token,
        });
        // Сервис отдаёт имена файлов, а не ссылки — как и в остальных
        // методах, превращаем их в адреса для показа.
        return (body.result || []).map(recipe => {
            recipe.imageHrefs = this.createImageHrefs(recipe.images);
            return recipe;
        });
    }

    async createPayment(object, token) {
        const body = await this.request({
            path: '/users/~/payments',
            method: 'post',
            body: {
                ...object,
                paymentIntegration: this.paymentIntegration
            },
            token,
        });
        return body.result;
    }

    async changeTariff(userId, tariffId, token) {
        const body = await this.request({
            path: `/users/${userId}/subscription/tariff`,
            method: 'put',
            body: {
                tariffId
            },
            token,
        });
        return body.result;
    }
}

module.exports = ApiClient;
